import React, { Component, ReactNode } from 'react';
import { FiAlertTriangle, FiRefreshCw, FiHome, FiInfo } from 'react-icons/fi';
import { logger } from '../../lib/logger';
import i18n from '../../lib/i18n';
import './ADHDErrorBoundary.scss';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  level?: 'page' | 'component' | 'feature';
  retryable?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string;
  retryCount: number;
  isRetrying: boolean;
}

export class ADHDErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: '',
      retryCount: 0,
      isRetrying: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { onError, level = 'component' } = this.props;
    
    logger.error('Error boundary caught error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      level,
      errorId: this.state.errorId,
      retryCount: this.state.retryCount
    });

    if (onError) {
      onError(error, errorInfo);
    }

    // Report to external error tracking service if configured
    this.reportError(error, errorInfo);
  }

  private reportError = (error: Error, errorInfo: React.ErrorInfo) => {
    // In a real app, send to error tracking service like Sentry
    // For now, just log comprehensively for debugging
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      errorId: this.state.errorId,
      level: this.props.level
    };

    // Could send to analytics or error tracking service
    console.error('Error Report:', errorReport);
  };

  private handleRetry = async () => {
    const { retryCount } = this.state;
    
    // Limit retry attempts to prevent infinite loops
    if (retryCount >= 3) {
      logger.warn('Max retry attempts reached', { 
        errorId: this.state.errorId,
        retryCount 
      });
      return;
    }

    this.setState({ isRetrying: true });
    
    logger.info('Retrying after error', { 
      errorId: this.state.errorId,
      retryCount: retryCount + 1 
    });

    // Add a small delay to let any async operations settle
    this.retryTimeoutId = window.setTimeout(() => {
      this.setState({
        hasError: false,
        error: null,
        retryCount: retryCount + 1,
        isRetrying: false
      });
    }, 1000);
  };

  private handleReload = () => {
    logger.info('User requested page reload', { 
      errorId: this.state.errorId 
    });
    window.location.reload();
  };

  private handleGoHome = () => {
    logger.info('User requested navigation to home', { 
      errorId: this.state.errorId 
    });
    window.location.href = '/';
  };

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private getADHDFriendlyMessage(error: Error | null, level: string) {
    if (!error) return i18n.t('errors.unexpected_error');

    // ADHD-friendly error messages based on common scenarios
    const message = error.message?.toLowerCase() || '';
    
    if (message.includes('network') || message.includes('fetch')) {
      return i18n.t('errors.connection_hiccup');
    }
    
    if (message.includes('auth') || message.includes('unauthorized')) {
      return i18n.t('errors.please_sign_in');
    }
    
    if (message.includes('rate limit') || message.includes('too many')) {
      return "Whoa, slow down speedy! You're working so fast you hit a limit. Take a breath and try again.";
    }
    
    if (level === 'component') {
      return "A small part of the app got confused. Let's refresh this section.";
    }
    
    if (level === 'feature') {
      return "This feature hit a snag. Don't worry, your data is safe!";
    }
    
    return i18n.t('errors.unexpected_error');
  }

  private getTroubleshootingTips(error: Error | null) {
    const message = error?.message?.toLowerCase() || '';
    
    if (message.includes('network') || message.includes('fetch')) {
      return [
        'Check your internet connection',
        'Try refreshing the page',
        'If using VPN, try disconnecting temporarily'
      ];
    }
    
    if (message.includes('auth')) {
      return [
        'Try signing out and back in',
        'Check if your session expired',
        'Clear browser cookies if the problem persists'
      ];
    }
    
    return [
      'Try the retry button - it often works!',
      'Refresh the page if retry doesn\'t help',
      'Your work is automatically saved, so no data lost'
    ];
  }

  render() {
    const { hasError, error, isRetrying, retryCount } = this.state;
    const { children, fallback, level = 'component', retryable = true } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      const adhdMessage = this.getADHDFriendlyMessage(error, level);
      const troubleshootingTips = this.getTroubleshootingTips(error);
      const canRetry = retryable && retryCount < 3;

      return (
        <div className={`adhd-error-boundary ${level}`}>
          <div className="error-content">
            <div className="error-icon">
              <FiAlertTriangle size={48} />
            </div>
            
            <h2 className="error-title">{i18n.t('errors.something_wrong')}</h2>
            
            <p className="error-message">{adhdMessage}</p>
            
            {level === 'page' && (
              <div className="error-details">
                <p className="error-subtitle">
                  Don't worry! Your ADHD assistant is still here to help.
                </p>
              </div>
            )}
            
            <div className="error-actions">
              {canRetry && (
                <button 
                  onClick={this.handleRetry}
                  disabled={isRetrying}
                  className="retry-button primary"
                  aria-label="Retry the action that failed"
                >
                  {isRetrying ? (
                    <>
                      <FiRefreshCw className="spinning" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <FiRefreshCw />
                      {i18n.t('buttons.try_again')}
                    </>
                  )}
                </button>
              )}
              
              <button 
                onClick={this.handleReload}
                className="reload-button secondary"
                aria-label="Reload the entire page"
              >
                <FiRefreshCw />
                {i18n.t('buttons.refresh_page')}
              </button>
              
              {level === 'page' && (
                <button 
                  onClick={this.handleGoHome}
                  className="home-button secondary"
                  aria-label="Go back to the home page"
                >
                  <FiHome />
                  {i18n.t('buttons.go_home')}
                </button>
              )}
            </div>
            
            <details className="troubleshooting">
              <summary>
                <FiInfo />
                Troubleshooting Tips
              </summary>
              <ul>
                {troubleshootingTips.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
              {process.env.NODE_ENV === 'development' && (
                <div className="error-debug">
                  <h4>Debug Info (Development Only)</h4>
                  <pre>{error?.stack}</pre>
                </div>
              )}
            </details>
            
            <div className="error-id">
              Error ID: <code>{this.state.errorId}</code>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}